import { isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";

import { HaNViewStoreContext, useRoomBookingTimeslot } from "@lib/logic";
import { markAsBookedLocally, type MeetingRoom, useAppDispatch } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { useCreateEvent, useFormatDate } from "@hooks";
import { MeetingRoomBookingDetailsContent, ModalPageBottom } from "@organisms";

import { useRoomsRequest } from "../../rooms/useRoomsRequest";
import { PanelStackContext } from "../../shared/PanelStack/PanelStackContext";
import { HaNContext } from "../../shell/HaNContext";
import { minuteClock } from "../../state/refresh/refreshClock";
import { HaNDetailsPage } from "../HaNDetailsPage";
import { useConfirmDiscardingBooking } from "../useConfirmDiscardingBooking";
import { useCompleteRoomBooking } from "./useCompleteRoomBooking";
import { useRoomBookingDraft } from "./useRoomBookingDraft";

// Books the room from now with a title, a message, invitees and an online meeting. Going back throws the changes away,
// after asking.
export const RoomBookingPage = ({ room }: { room: MeetingRoom }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { rooms } = useRequiredContext(HaNContext);
  const { push, pop } = useRequiredContext(PanelStackContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const request = useRoomsRequest();
  const { draft, edit } = useRoomBookingDraft(room);
  const timeslot = useRoomBookingTimeslot({
    minuteClock,
    request,
    roomId: room.id,
    durationMinutes: rooms.duration,
  });
  const confirmDiscardingBooking = useConfirmDiscardingBooking();
  const completeRoomBooking = useCompleteRoomBooking();

  const startDateTime = timeslot?.start.toISOString() ?? "";
  const endDateTime = timeslot?.end.toISOString() ?? "";
  const endsSameDay = !!timeslot && isSameDay(timeslot.start, timeslot.end);
  const formattedStart = useFormatDate(startDateTime, "dd MMMM HH:mm");
  const formattedEnd = useFormatDate(endDateTime, endsSameDay ? "HH:mm" : "dd MMMM HH:mm");

  const { createEvent, isCreatingEvent } = useCreateEvent(() => {
    dispatch(markAsBookedLocally({ buildingId: room.buildingId, roomId: room.id }));
    completeRoomBooking(room.id);
  });

  const goBack = () => {
    confirmDiscardingBooking(() => {
      viewStore.discardBookingDraft();
      pop();
    });
  };

  const book = () => {
    createEvent(
      {
        title: draft.title,
        message: draft.message,
        attendees: draft.invitees.map(invitee => invitee.email),
        startDateTime,
        endDateTime,
        isOnlineMeeting: draft.isOnlineMeeting,
        rooms: [room.email],
        isAllDay: false,
        organizerTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        origin: "BookRoomNow",
      },
      t("meeting:CreateMeetingSuccessMessage", { startDate: formattedStart, endDate: formattedEnd }),
    );
  };

  return (
    <HaNDetailsPage
      title={t("screen:MeetingRoomDetails")}
      onBack={goBack}
      footer={
        <ModalPageBottom
          className="p-0"
          buttonLabel={t("meeting:CreateMeetingCreateEventButton")}
          buttonDisabled={!draft.title || !timeslot}
          buttonLoading={isCreatingEvent}
          onClick={book}
        />
      }>
      <MeetingRoomBookingDetailsContent
        room={room}
        dynamicTimeslot={timeslot}
        title={draft.title}
        message={draft.message}
        attendees={draft.invitees}
        isOnlineMeeting={draft.isOnlineMeeting}
        onTitleChange={title => edit({ title })}
        onMessageChange={message => edit({ message })}
        onIsOnlineMeetingChange={isOnlineMeeting => edit({ isOnlineMeeting })}
        onInviteesClick={() => push("invitees")}
      />
    </HaNDetailsPage>
  );
};
